import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './TreeView.css';

const TreeView = ({ onSelectDocumento }) => {
  const { user } = useAuth();
  const [estrutura, setEstrutura] = useState({});
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [diretoriosCarregados, setDiretoriosCarregados] = useState({});
  const [itemSelecionado, setItemSelecionado] = useState(null);
  const [conteudoSelecionado, setConteudoSelecionado] = useState(null);
  const [loadingConteudo, setLoadingConteudo] = useState(false);

  useEffect(() => {
    if (user) {
      carregarEstrutura();
    }
  }, [user]);

  // Recarregar dados quando a página receber foco
  useEffect(() => {
    const handleFocus = () => {
      if (user) {
        carregarEstrutura();
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user]);

  const carregarEstrutura = async (caminho = '') => {
    try {
      if (caminho === '') {
        setLoading(true);
      }
      
      const res = await api.get('/protocolos/estrutura', {
        params: { caminho }
      }).catch((err) => {
        // Log apenas em desenvolvimento
        if (process.env.NODE_ENV === 'development') {
          console.error('Erro ao buscar estrutura:', err);
        }
        return { data: { success: false, data: { itens: [] } } };
      });
      
      if (res.data?.success) {
        const dados = res.data.data;
        // SEMPRE usar o caminho retornado pela API como chave
        // Se a API retornou um caminho (ex: "2025"), usar esse
        // Se não retornou caminho, usar o caminho solicitado ou ''
        const caminhoChave = dados.caminho !== undefined ? dados.caminho : (caminho || '');
        
        // Log removido para produção
        
        const estruturaOrganizada = organizarEstrutura(dados.itens || []);
        
        setDiretoriosCarregados(prev => ({ ...prev, [caminhoChave]: true }));
        setEstrutura(prev => {
          const novo = { ...prev };
          novo[caminhoChave] = estruturaOrganizada;
          return novo;
        });
        
        // Expandir primeiro nível na primeira carga
        // IMPORTANTE: Usar '' como chave, não 'root', para manter consistência
        if (caminho === '' && dados.itens && dados.itens.length > 0) {
          setExpanded(prev => ({ ...prev, '': true }));
        }
      } else {
        // Log removido para produção
        // Mesmo em caso de erro, inicializar estrutura vazia para evitar loops
        if (caminho === '') {
          setEstrutura({ '': { diretorios: [], arquivos: [] } });
        }
      }
    } catch (error) {
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.error('Erro ao carregar estrutura:', error);
      }
      // Em caso de erro, inicializar estrutura vazia
      if (caminho === '') {
        setEstrutura({ '': { diretorios: [], arquivos: [] } });
      }
    } finally {
      if (caminho === '') {
        setLoading(false);
      }
    }
  };

  const organizarEstrutura = (itens, caminhoPai = '') => {
    const diretorios = [];
    const arquivos = [];
    
    itens.forEach(item => {
      if (item.tipo === 'diretorio') {
        diretorios.push(item);
      } else if (item.tipo === 'arquivo') {
        arquivos.push(item);
      }
    });
    
    // Ordenar diretórios e arquivos por nome
    diretorios.sort((a, b) => a.nome.localeCompare(b.nome));
    arquivos.sort((a, b) => a.nome.localeCompare(b.nome));
    
    return { diretorios, arquivos };
  };

  const toggleExpand = async (caminho) => {
    const key = caminho || 'root';
    const isExpanded = expanded[key];
    
    setExpanded(prev => ({
      ...prev,
      [key]: !isExpanded
    }));
    
    // Se está expandindo e ainda não carregou este diretório, carregar
    if (!isExpanded && !diretoriosCarregados[caminho]) {
      await carregarEstrutura(caminho);
    }
  };

  const formatarTamanho = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatarData = (data) => {
    if (!data) return '';
    const d = new Date(data);
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const handleDownloadArquivo = async (caminho) => {
    try {
      const response = await api.get(`/protocolos/arquivo/${caminho}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', caminho.split(/[\/\\]/).pop());
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.error('Erro ao baixar arquivo:', error);
      }
      alert('Erro ao baixar arquivo');
    }
  };

  const carregarConteudoItem = async (item, caminho) => {
    try {
      setLoadingConteudo(true);
      setItemSelecionado({ ...item, caminho });
      
      if (item.tipo === 'arquivo') {
        // Para arquivos, buscar o arquivo e preparar para visualização
        const response = await api.get(`/protocolos/arquivo/${caminho}`, {
          responseType: 'blob'
        });
        
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const tipoMime = response.headers['content-type'] || 'application/octet-stream';
        
        let conteudoTexto = null;
        // Se for arquivo de texto, ler o conteúdo
        if (tipoMime.startsWith('text/')) {
          try {
            const texto = await blob.text();
            conteudoTexto = texto;
          } catch (e) {
            // Se não conseguir ler como texto, deixar null
          }
        }
        
        setConteudoSelecionado({
          tipo: 'arquivo',
          nome: item.nome,
          caminho: caminho,
          url: url,
          tipoMime: tipoMime,
          tamanho: item.tamanho,
          modificado: item.modificado,
          conteudoTexto: conteudoTexto
        });
      } else if (item.tipo === 'diretorio') {
        // Para diretórios, buscar todo o conteúdo
        const response = await api.get('/protocolos/estrutura', {
          params: { caminho }
        });
        
        if (response.data?.success) {
          const dados = response.data.data;
          const estruturaOrganizada = organizarEstrutura(dados.itens || []);
          
          setConteudoSelecionado({
            tipo: 'diretorio',
            nome: item.nome,
            caminho: caminho,
            diretorios: estruturaOrganizada.diretorios || [],
            arquivos: estruturaOrganizada.arquivos || []
          });
        }
      }
    } catch (error) {
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.error('Erro ao carregar conteúdo:', error);
      }
      setConteudoSelecionado({
        tipo: 'erro',
        mensagem: 'Erro ao carregar conteúdo'
      });
    } finally {
      setLoadingConteudo(false);
    }
  };

  const handleItemClick = (e, item, caminho) => {
    e.stopPropagation();
    carregarConteudoItem(item, caminho);
  };

  const renderizarEstrutura = (itens, caminhoPai = '', nivel = 0) => {
    if (!itens) {
      if (process.env.NODE_ENV === 'development') {
        // Log removido para produção
      }
      return null;
    }

    const { diretorios = [], arquivos = [] } = itens;
    
    const key = caminhoPai || 'root';
    // Na raiz (nivel 0), sempre mostrar se tiver dados
    // Para subdiretórios, verificar se está expandido
    const isExpanded = nivel === 0 ? true : (expanded[key] === true);

    return (
      <div className="treeview-children" style={{ marginLeft: nivel > 0 ? '1.5rem' : '0' }}>
        {/* Diretórios */}
        {diretorios.map((dir) => {
          // Usar o caminho do item se disponível, senão construir
          const caminhoCompleto = dir.caminho || (caminhoPai ? `${caminhoPai}/${dir.nome}` : dir.nome);
          const dirKey = caminhoCompleto;
          const dirExpanded = expanded[dirKey];
          const temEstrutura = estrutura[caminhoCompleto];

          return (
            <div key={dirKey} className="treeview-node">
              <div
                className={`treeview-item treeview-folder ${itemSelecionado?.caminho === caminhoCompleto ? 'treeview-selected' : ''}`}
                onClick={(e) => {
                  toggleExpand(caminhoCompleto);
                  handleItemClick(e, dir, caminhoCompleto);
                }}
                style={{ paddingLeft: `${nivel * 0.5}rem` }}
              >
                <span className="treeview-icon">
                  {dirExpanded ? '📂' : '📁'}
                </span>
                <span className="treeview-label">{dir.nome}</span>
              </div>
              
              {dirExpanded && temEstrutura && (
                renderizarEstrutura(temEstrutura, caminhoCompleto, nivel + 1)
              )}
              {dirExpanded && !temEstrutura && !diretoriosCarregados[caminhoCompleto] && (
                <div className="treeview-loading-item" style={{ paddingLeft: `${(nivel + 1) * 0.5}rem` }}>
                  Carregando...
                </div>
              )}
            </div>
          );
        })}

        {/* Arquivos */}
        {arquivos.map((arquivo) => {
          const caminhoArquivo = arquivo.caminho;
          return (
            <div
              key={caminhoArquivo}
              className={`treeview-item treeview-documento ${itemSelecionado?.caminho === caminhoArquivo ? 'treeview-selected' : ''}`}
              onClick={(e) => handleItemClick(e, arquivo, caminhoArquivo)}
              style={{ paddingLeft: `${nivel * 0.5}rem` }}
              title={`${arquivo.nome} - ${formatarTamanho(arquivo.tamanho)} - ${formatarData(arquivo.modificado)}`}
            >
              <span className="treeview-icon">📄</span>
              <span className="treeview-label">{arquivo.nome}</span>
              {arquivo.tamanho && (
                <span className="treeview-size" style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#6B7280' }}>
                  ({formatarTamanho(arquivo.tamanho)})
                </span>
              )}
            </div>
          );
        })}

        {diretorios.length === 0 && arquivos.length === 0 && (
          <div className="treeview-empty-item" style={{ paddingLeft: `${nivel * 0.5}rem` }}>
            <span className="treeview-label" style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
              Diretório vazio
            </span>
          </div>
        )}
      </div>
    );
  };

  if (loading && Object.keys(estrutura).length === 0) {
    return (
      <div className="treeview-loading">
        <div className="spinner"></div>
        <span>Carregando estrutura...</span>
      </div>
    );
  }

  const renderizarConteudo = () => {
    if (!conteudoSelecionado) {
      return (
        <div className="treeview-panel-empty">
          <div className="treeview-panel-empty-icon">📋</div>
          <p>Selecione um item para visualizar o conteúdo</p>
        </div>
      );
    }

    if (loadingConteudo) {
      return (
        <div className="treeview-panel-loading">
          <div className="spinner"></div>
          <span>Carregando conteúdo...</span>
        </div>
      );
    }

    if (conteudoSelecionado.tipo === 'erro') {
      return (
        <div className="treeview-panel-error">
          <span className="treeview-panel-error-icon">⚠️</span>
          <p>{conteudoSelecionado.mensagem}</p>
        </div>
      );
    }

    if (conteudoSelecionado.tipo === 'diretorio') {
      return (
        <div className="treeview-panel-content">
          <div className="treeview-panel-header">
            <h3>📁 {conteudoSelecionado.nome}</h3>
            <p className="treeview-panel-path">{conteudoSelecionado.caminho}</p>
          </div>
          <div className="treeview-panel-body">
            {conteudoSelecionado.diretorios.length > 0 && (
              <div className="treeview-panel-section">
                <h4>Diretórios ({conteudoSelecionado.diretorios.length})</h4>
                <div className="treeview-panel-list">
                  {conteudoSelecionado.diretorios.map((dir, idx) => (
                    <div
                      key={idx}
                      className="treeview-panel-item treeview-panel-folder"
                      onClick={(e) => {
                        const caminhoCompleto = dir.caminho || `${conteudoSelecionado.caminho}/${dir.nome}`;
                        handleItemClick(e, dir, caminhoCompleto);
                      }}
                    >
                      <span className="treeview-panel-item-icon">📁</span>
                      <span className="treeview-panel-item-name">{dir.nome}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {conteudoSelecionado.arquivos.length > 0 && (
              <div className="treeview-panel-section">
                <h4>Arquivos ({conteudoSelecionado.arquivos.length})</h4>
                <div className="treeview-panel-list">
                  {conteudoSelecionado.arquivos.map((arquivo, idx) => (
                    <div
                      key={idx}
                      className="treeview-panel-item treeview-panel-file"
                      onClick={(e) => handleItemClick(e, arquivo, arquivo.caminho)}
                    >
                      <span className="treeview-panel-item-icon">📄</span>
                      <span className="treeview-panel-item-name">{arquivo.nome}</span>
                      {arquivo.tamanho && (
                        <span className="treeview-panel-item-size">{formatarTamanho(arquivo.tamanho)}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {conteudoSelecionado.diretorios.length === 0 && conteudoSelecionado.arquivos.length === 0 && (
              <div className="treeview-panel-empty">
                <p>Diretório vazio</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (conteudoSelecionado.tipo === 'arquivo') {
      const podeVisualizar = conteudoSelecionado.tipoMime.startsWith('image/') ||
                            conteudoSelecionado.tipoMime === 'application/pdf' ||
                            conteudoSelecionado.tipoMime.startsWith('text/');

      return (
        <div className="treeview-panel-content">
          <div className="treeview-panel-header">
            <h3>📄 {conteudoSelecionado.nome}</h3>
            <div className="treeview-panel-file-info">
              <span className="treeview-panel-file-meta">
                {formatarTamanho(conteudoSelecionado.tamanho)}
              </span>
              <span className="treeview-panel-file-separator">•</span>
              <span className="treeview-panel-file-meta">
                {formatarData(conteudoSelecionado.modificado)}
              </span>
            </div>
            <div className="treeview-panel-actions">
              <button
                className="treeview-panel-btn treeview-panel-btn-download"
                onClick={() => handleDownloadArquivo(conteudoSelecionado.caminho)}
              >
                📥 Baixar
              </button>
            </div>
          </div>
          <div className="treeview-panel-body treeview-panel-file-viewer">
            {podeVisualizar ? (
              <div className="treeview-panel-file-content">
                {conteudoSelecionado.tipoMime.startsWith('image/') && (
                  <img src={conteudoSelecionado.url} alt={conteudoSelecionado.nome} />
                )}
                {conteudoSelecionado.tipoMime === 'application/pdf' && (
                  <iframe src={conteudoSelecionado.url} title={conteudoSelecionado.nome} />
                )}
                {conteudoSelecionado.tipoMime.startsWith('text/') && conteudoSelecionado.conteudoTexto && (
                  <pre className="treeview-panel-text-content">{conteudoSelecionado.conteudoTexto}</pre>
                )}
                {conteudoSelecionado.tipoMime.startsWith('text/') && !conteudoSelecionado.conteudoTexto && (
                  <div className="treeview-panel-file-preview">
                    <div className="treeview-panel-file-preview-icon">📄</div>
                    <p>Carregando conteúdo do arquivo...</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="treeview-panel-file-preview">
                <div className="treeview-panel-file-preview-icon">📄</div>
                <p>Visualização não disponível para este tipo de arquivo</p>
                <p className="treeview-panel-file-preview-type">{conteudoSelecionado.tipoMime}</p>
                <button
                  className="treeview-panel-btn treeview-panel-btn-primary"
                  onClick={() => handleDownloadArquivo(conteudoSelecionado.caminho)}
                >
                  📥 Baixar Arquivo
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="treeview-container">
      <div className="treeview">
        <div className="treeview-header">
          <h3>Estrutura Organizacional</h3>
        </div>
        <div className="treeview-content">
        {loading ? (
          <div className="treeview-loading">
            <div className="spinner"></div>
            <span>Carregando estrutura...</span>
          </div>
        ) : (() => {
          // ============================================
          // LÓGICA CORRETA: Buscar estrutura raiz
          // ============================================
          // A API sempre retorna caminho='' para a raiz
          // Estrutura raiz deve estar em estrutura['']
          // ============================================
          const estruturaRaiz = estrutura[''];
          
          // Se não encontrou estrutura raiz, verificar se ainda não carregou
          if (!estruturaRaiz) {
            // Se ainda não carregou a raiz, mostrar loading
            if (!diretoriosCarregados['']) {
              return (
                <div className="treeview-loading">
                  <div className="spinner"></div>
                  <span>Carregando estrutura...</span>
                </div>
              );
            }
            
            // Se já tentou carregar mas não encontrou, mostrar mensagem
            return (
              <div className="treeview-empty">
                Nenhum diretório disponível
              </div>
            );
          }
          
          const temDiretorios = estruturaRaiz.diretorios && estruturaRaiz.diretorios.length > 0;
          const temArquivos = estruturaRaiz.arquivos && estruturaRaiz.arquivos.length > 0;
          
          if (!temDiretorios && !temArquivos) {
            return (
              <div className="treeview-empty">
                Nenhum diretório disponível
              </div>
            );
          }
          
          return renderizarEstrutura(estruturaRaiz, '', 0);
        })()}
        </div>
      </div>
      <div className="treeview-panel">
        {renderizarConteudo()}
      </div>
    </div>
  );
};

export default TreeView;
